// Local storage utility for managing users
export interface StoredUser {
    id: string;
    name: string;
    email: string;
    role: 'Super Admin' | 'Admin' | 'Moderator' | 'User';
    status: 'Active' | 'Inactive' | 'Suspended';
    lastLogin: string;
    avatar: string;
    createdDate: string;
}

const STORAGE_KEY = 'amasqis_users';

// Initialize with default admin users if storage is empty
const initializeDefaultUsers = (): StoredUser[] => {
    return [
        {
            id: '1',
            name: 'Admin User',
            email: 'admin@amasqis.ai',
            role: 'Super Admin',
            status: 'Active',
            lastLogin: new Date().toISOString(),
            avatar: 'AU',
            createdDate: new Date().toISOString()
        },
        {
            id: '2',
            name: 'John Doe',
            email: 'john.doe@amasqis.ai',
            role: 'Admin',
            status: 'Active',
            lastLogin: new Date(Date.now() - 86400000).toISOString(), // 1 day ago
            avatar: 'JD',
            createdDate: new Date(Date.now() - 604800000).toISOString() // 7 days ago
        },
        {
            id: '3',
            name: 'Jane Smith',
            email: 'jane.smith@amasqis.ai',
            role: 'Moderator',
            status: 'Active',
            lastLogin: new Date(Date.now() - 259200000).toISOString(), // 3 days ago
            avatar: 'JS',
            createdDate: new Date(Date.now() - 1209600000).toISOString() // 14 days ago
        }
    ];
};

export const userStorage = {
    // Get all users
    getAll(): StoredUser[] {
        try {
            const data = localStorage.getItem(STORAGE_KEY);
            if (!data) {
                // Initialize with default users
                const defaultUsers = initializeDefaultUsers();
                localStorage.setItem(STORAGE_KEY, JSON.stringify(defaultUsers));
                return defaultUsers;
            }
            return JSON.parse(data);
        } catch (error) {
            console.error('Error reading users from localStorage:', error);
            return initializeDefaultUsers();
        }
    },

    // Get active users (logged in within last 30 days)
    getActiveUsers(): StoredUser[] {
        const users = this.getAll();
        const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
        return users.filter(user => {
            const lastLogin = new Date(user.lastLogin).getTime();
            return user.status === 'Active' && lastLogin > thirtyDaysAgo;
        });
    },

    // Add new user
    add(user: Omit<StoredUser, 'id' | 'createdDate'>): StoredUser {
        try {
            const users = this.getAll();
            const newUser: StoredUser = {
                ...user,
                id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
                createdDate: new Date().toISOString(),
            };
            users.unshift(newUser); // Add to beginning
            localStorage.setItem(STORAGE_KEY, JSON.stringify(users));
            return newUser;
        } catch (error) {
            console.error('Error adding user to localStorage:', error);
            throw error;
        }
    },

    // Update user
    update(id: string, updates: Partial<StoredUser>): StoredUser | null {
        try {
            const users = this.getAll();
            const index = users.findIndex(user => user.id === id);
            if (index === -1) return null;
            
            users[index] = { ...users[index], ...updates };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(users));
            return users[index];
        } catch (error) {
            console.error('Error updating user:', error);
            return null;
        }
    },

    // Update last login
    updateLastLogin(id: string): StoredUser | null {
        return this.update(id, { lastLogin: new Date().toISOString() });
    },

    // Delete user
    delete(id: string): boolean {
        try {
            const users = this.getAll();
            const filtered = users.filter(user => user.id !== id);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
            return true;
        } catch (error) {
            console.error('Error deleting user:', error);
            return false;
        }
    },

    // Get single user
    getById(id: string): StoredUser | null {
        const users = this.getAll();
        return users.find(user => user.id === id) || null;
    },

    // Clear all users (useful for testing)
    clear(): void {
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch (error) {
            console.error('Error clearing users:', error);
        }
    }
};
